/**
 * Home-page templates of a site (internal/sites-broadcast-template-contract.md).
 * `template` is owned by castr (`Site.template`, default `catalog`); the
 * runtime reads it from the published record. Everything the dashboard shows
 * or hides per template is derived from these constants.
 */
export const TEMPLATE_CATALOG = 'catalog'
export const TEMPLATE_BROADCAST = 'broadcast'

export const SITE_TEMPLATES = [
	{
		id: TEMPLATE_CATALOG,
		title: 'Video library',
		name: 'Video library',
		description: 'A streaming library: featured hero, categories, search, live and on-demand.',
	},
	{
		id: TEMPLATE_BROADCAST,
		title: 'Broadcast (live event or service)',
		// short form for running text ("Switch to Broadcast")
		name: 'Broadcast',
		description: 'One big live player with chat, a schedule, an About block, an optional registration form and donations. Made for events, conferences and churches.',
	},
]

/** castr's `template` with the catalog fallback the runtime applies to old records. */
export function siteTemplate(site) {
	return site?.template === TEMPLATE_BROADCAST ? TEMPLATE_BROADCAST : TEMPLATE_CATALOG
}

export const isBroadcast = (site) => siteTemplate(site) === TEMPLATE_BROADCAST

export const templateMeta = (id) => SITE_TEMPLATES.find((t) => t.id === id) || SITE_TEMPLATES[0]

/**
 * Theme presets of the broadcast template (contract §2.3, D9): plain
 * `colors.{background,text,primary}` values, no runtime code. The light one
 * is what castr seeds at creation. Every preset keeps text ≥ 15:1 and the
 * accent ≥ 4.5:1 on its background (and white / black labels ≥ 4.5:1 on it).
 */
export const BROADCAST_THEME_PRESETS = [
	{ id: 'broadcast-light', title: 'Broadcast Light', description: 'Warm white page, dark text, deep blue accent. Pair with the light chat preset on the stream.', colors: { background: '#FAF8F4', text: '#1C1917', primary: '#1D4ED8' } },
	{ id: 'broadcast-dark', title: 'Broadcast Dark', description: 'Near-black page, light text, sky-blue accent. Pair with the dark chat preset on the stream.', colors: { background: '#0F1115', text: '#F3F4F6', primary: '#60A5FA' } },
	{ id: 'broadcast-warm', title: 'Warm', description: 'Cream page, espresso text, terracotta accent. Pair with the light chat preset.', colors: { background: '#FBF5EC', text: '#2B1D14', primary: '#9C4221' } },
	{ id: 'broadcast-navy', title: 'Navy', description: 'Cool white page, slate text, navy accent. Pair with the light chat preset.', colors: { background: '#F7F9FC', text: '#0F172A', primary: '#1E3A8A' } },
	{ id: 'broadcast-forest', title: 'Forest', description: 'Soft white page, deep green text, forest-green accent. Pair with the light chat preset.', colors: { background: '#F6F8F4', text: '#14201A', primary: '#1F6F4A' } },
	{ id: 'broadcast-midnight', title: 'Midnight', description: 'Midnight-blue page, light text, gold accent. Pair with the dark chat preset.', colors: { background: '#0B1020', text: '#F1F5F9', primary: '#D4A72C' } },
	{ id: 'broadcast-plum', title: 'Plum', description: 'Warm white page, aubergine text, plum accent. Pair with the light chat preset.', colors: { background: '#FBF8FB', text: '#241726', primary: '#7E3A8C' } },
]

/** Management pages the broadcast template does not use (deep links still resolve, with a notice). */
export const BROADCAST_HIDDEN_ROUTES = ['SiteCategories', 'SiteFeatured']

/** Routes that only exist for the broadcast template. */
export const BROADCAST_ONLY_ROUTES = ['SiteBroadcast', 'SiteAudience', 'SiteDonations']

/** Settings › General switch confirmation copy (contract §7). */
export const SWITCH_COPY = {
	[TEMPLATE_BROADCAST]: {
		title: 'Switch to Broadcast?',
		body: 'Your site changes immediately: the featured hero, categories, search and watchlist are hidden and the home page shows your live stream with service times, About and Give. Your library, plans, promo codes, viewers and domains are kept. You can switch back any time.',
		confirm: 'Switch to Broadcast',
	},
	[TEMPLATE_CATALOG]: {
		title: 'Switch to Video library?',
		body: 'Your site changes immediately: the home page shows the featured hero, categories and search again, and the live stream, service times, About and Give blocks are hidden. Your library, plans, promo codes, viewers and domains are kept. You can switch back any time.',
		confirm: 'Switch to Video library',
	},
}

/** Giving modes (runtime `givingMode`, contract §2.2). External giving links are not offered. */
export const GIVING_MODES = [
	{ id: 'off', label: 'Off', description: 'No Give button on the site.' },
	{ id: 'native', label: 'Donations', description: 'A Give button opens the donation form on your site; viewers give with a card and the money goes to your Stripe account.' },
]

export const GIVING_DEFAULT_BUTTON_LABEL = 'Give'
export const GIVING_DEFAULT_THANK_YOU = 'Thank you for your gift.'
export const GIVING_PRESETS_MAX = 6
export const GIVING_AMOUNT_CAP = 10000
export const GIVING_MIN_AMOUNT_FLOOR = 1

/** Currencies offered for native donations (paywall validates any 3-letter code; this is the picker). */
export const GIVING_CURRENCIES = ['USD', 'EUR', 'GBP', 'CAD', 'AUD', 'NZD', 'CHF', 'SEK', 'NOK', 'DKK', 'PLN', 'CZK', 'HUF', 'RON', 'BGN', 'JPY', 'SGD', 'HKD', 'MXN', 'BRL', 'ZAR', 'INR', 'PHP', 'MYR', 'THB', 'AED', 'NGN', 'KES']

/** Weekdays for the service-times editor (runtime `weekday`: 0 = Sunday). */
export const WEEKDAYS = [
	{ value: 0, text: 'Sunday', short: 'Sun' },
	{ value: 1, text: 'Monday', short: 'Mon' },
	{ value: 2, text: 'Tuesday', short: 'Tue' },
	{ value: 3, text: 'Wednesday', short: 'Wed' },
	{ value: 4, text: 'Thursday', short: 'Thu' },
	{ value: 5, text: 'Friday', short: 'Fri' },
	{ value: 6, text: 'Saturday', short: 'Sat' },
]

export const SERVICE_TIMES_MAX = 14
export const SERVICE_DURATION_MIN = 15
export const SERVICE_DURATION_MAX = 480

/**
 * Browsers (ICU) still list a few zones under their pre-rename IANA names;
 * the picker shows and saves the current names (every `Intl` API accepts
 * both, so an already-saved legacy name keeps resolving). Only true renames —
 * zones IANA merged into a neighbour stay distinct. Keep in sync with
 * `TIME_ZONE_RENAMES` in ott-cms `lib/sites/broadcast-schedule.ts`.
 */
const ZONE_ALIASES = {
	'Asia/Calcutta': 'Asia/Kolkata',
	'Asia/Saigon': 'Asia/Ho_Chi_Minh',
	'Asia/Katmandu': 'Asia/Kathmandu',
	'Asia/Rangoon': 'Asia/Yangon',
	'Asia/Dacca': 'Asia/Dhaka',
	'Asia/Macao': 'Asia/Macau',
	'Asia/Thimbu': 'Asia/Thimphu',
	'Asia/Ulan_Bator': 'Asia/Ulaanbaatar',
	'Asia/Ashkhabad': 'Asia/Ashgabat',
	'Asia/Ujung_Pandang': 'Asia/Makassar',
	'Europe/Kiev': 'Europe/Kyiv',
	'America/Buenos_Aires': 'America/Argentina/Buenos_Aires',
	'America/Catamarca': 'America/Argentina/Catamarca',
	'America/Cordoba': 'America/Argentina/Cordoba',
	'America/Jujuy': 'America/Argentina/Jujuy',
	'America/Mendoza': 'America/Argentina/Mendoza',
	'America/Indianapolis': 'America/Indiana/Indianapolis',
	'America/Louisville': 'America/Kentucky/Louisville',
	'America/Godthab': 'America/Nuuk',
	'America/Coral_Harbour': 'America/Atikokan',
	'Africa/Asmera': 'Africa/Asmara',
	'Atlantic/Faeroe': 'Atlantic/Faroe',
	'Pacific/Truk': 'Pacific/Chuuk',
	'Pacific/Ponape': 'Pacific/Pohnpei',
	'Pacific/Enderbury': 'Pacific/Kanton',
}

/** The current IANA name of a zone (`Asia/Saigon` → `Asia/Ho_Chi_Minh`); '' for empty input. */
export function canonicalTimeZone(zone) {
	const z = typeof zone === 'string' ? zone.trim() : ''
	return ZONE_ALIASES[z] || z
}

/** IANA zone names: the browser's list when available, a short fallback otherwise. */
function timeZoneNames(current = '') {
	current = canonicalTimeZone(current)
	let zones = []
	try {
		zones = typeof Intl.supportedValuesOf === 'function' ? Intl.supportedValuesOf('timeZone') : []
	} catch {
		zones = []
	}
	if (!zones.length) {
		zones = ['UTC', 'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles', 'America/Toronto', 'America/Sao_Paulo', 'Europe/London', 'Europe/Paris', 'Europe/Berlin', 'Europe/Helsinki', 'Africa/Lagos', 'Africa/Johannesburg', 'Asia/Dubai', 'Asia/Kolkata', 'Asia/Singapore', 'Asia/Manila', 'Asia/Tokyo', 'Australia/Sydney', 'Pacific/Auckland']
	}
	zones = [...new Set(zones.map((z) => ZONE_ALIASES[z] || z))]
	if (!zones.includes('UTC')) zones = ['UTC', ...zones]
	if (current && !zones.includes(current)) zones = [current, ...zones]
	return zones
}

/** The browser's zone (`Intl`), or '' when unknown. */
export function browserTimeZone() {
	try {
		const tz = Intl.DateTimeFormat().resolvedOptions().timeZone
		return typeof tz === 'string' && tz ? canonicalTimeZone(tz) : ''
	} catch {
		return ''
	}
}

const offsetCache = new Map()
/**
 * Current UTC offset of a zone in minutes (east positive), or null when the
 * zone is unknown to this browser. Computed from `Intl` wall-clock parts so it
 * follows DST for "now".
 */
export function timeZoneOffsetMinutes(zone, at = new Date()) {
	const key = `${zone}|${Math.floor(at.getTime() / 3600000)}`
	if (offsetCache.has(key)) return offsetCache.get(key)
	let minutes = null
	try {
		const parts = new Intl.DateTimeFormat('en-US', { timeZone: zone, hourCycle: 'h23', year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric', second: 'numeric' }).formatToParts(at)
		const get = (type) => Number(parts.find((p) => p.type === type)?.value)
		const wall = Date.UTC(get('year'), get('month') - 1, get('day'), get('hour') % 24, get('minute'), get('second'))
		minutes = Math.round((wall - Math.floor(at.getTime() / 1000) * 1000) / 60000)
	} catch {
		minutes = null
	}
	offsetCache.set(key, minutes)
	return minutes
}

/** "GMT-5", "GMT+5:30", "GMT+0" — what viewers recognise next to a city. */
export function formatUtcOffset(minutes) {
	if (!Number.isFinite(minutes)) return ''
	const sign = minutes < 0 ? '-' : '+'
	const abs = Math.abs(minutes)
	const h = Math.floor(abs / 60)
	const m = abs % 60
	return `GMT${sign}${h}${m ? `:${String(m).padStart(2, '0')}` : ''}`
}

const REGION_ORDER = ['UTC', 'America', 'Europe', 'Africa', 'Asia', 'Australia', 'Pacific', 'Atlantic', 'Indian', 'Antarctica', 'Arctic']
/**
 * Zones for the searchable picker, grouped by IANA region (UTC first, then
 * the Americas, Europe, Africa, Asia, Oceania…), alphabetical inside a
 * region, each with its current offset: `{ value, text, region, offsetMinutes,
 * offsetLabel, label, search }`. `label` is "America/Chicago (GMT-5)".
 */
export function timeZoneEntries(current = '', at = new Date()) {
	const entries = timeZoneNames(current).map((zone) => {
		const slash = zone.indexOf('/')
		const region = zone === 'UTC' ? 'UTC' : slash > 0 ? zone.slice(0, slash) : 'Other'
		const offsetMinutes = timeZoneOffsetMinutes(zone, at)
		const offsetLabel = formatUtcOffset(offsetMinutes)
		const text = zone.replace(/_/g, ' ')
		const label = offsetLabel ? `${text} (${offsetLabel})` : text
		return {
			value: zone,
			text,
			region,
			offsetMinutes,
			offsetLabel,
			label,
			search: `${text} ${offsetLabel} ${offsetLabel.replace('GMT', 'UTC')}`.toLowerCase().replace(/[_/]+/g, ' '),
		}
	})
	const rank = (region) => {
		const i = REGION_ORDER.indexOf(region)
		return i === -1 ? REGION_ORDER.length : i
	}
	return entries.sort((a, b) => rank(a.region) - rank(b.region) || a.region.localeCompare(b.region) || a.text.localeCompare(b.text))
}

/** "America/Chicago (GMT-5)" for a stored zone (an old name shows as the current one); the bare name when the browser does not know it. */
export function timeZoneLabel(zone, entries = null) {
	zone = canonicalTimeZone(zone)
	if (!zone) return ''
	const hit = (entries || timeZoneEntries(zone)).find((z) => z.value === zone)
	if (hit) return hit.label
	const offset = formatUtcOffset(timeZoneOffsetMinutes(zone))
	const text = String(zone).replace(/_/g, ' ')
	return offset ? `${text} (${offset})` : text
}

/**
 * Runtime settings keys owned by Settings › Broadcast (contract §2.2); the
 * five giving presentation keys are saved through castr's `PUT /giving`.
 */
export const BROADCAST_SETTING_KEYS = [
	'broadcastTagline', 'broadcastTimezone', 'broadcastServiceTimes', 'broadcastAbout', 'broadcastAddress',
	'broadcastLiveItemId', 'broadcastRecentServicesEnabled', 'broadcastRecentServicesCategoryId',
]
export const GIVING_SETTING_KEYS = ['givingMode', 'givingExternalUrl', 'givingButtonLabel', 'givingThankYouMessage', 'broadcastGivingMessage']
